(function (global) {
  'use strict';
  const MAX_FILE = 10 * 1024 * 1024;
  const initialArea = () => ({ x: .3, y: .2, width: .4, height: .4 });
  function constrain(a) {
    const width = Math.max(.03, Math.min(1, a.width));
    const height = Math.max(.03, Math.min(1, a.height));
    return { x: Math.max(0, Math.min(1 - width, a.x)), y: Math.max(0, Math.min(1 - height, a.y)), width, height };
  }
  function canvasBlob(canvas, type, current, quality) {
    return new Promise((resolve, reject) => {
      const finish = value => { clearTimeout(timer); resolve(value); };
      // Some browsers leave the async canvas encoder pending. Keep the same pixels.
      const timer = setTimeout(() => {
        if (!current()) { finish(null); return; }
        try {
          const bytes = Uint8Array.from(atob(canvas.toDataURL(type, quality).split(',')[1]), c => c.charCodeAt(0));
          finish(new Blob([bytes], { type }));
        } catch (error) { reject(error); }
      }, 1500);
      try { canvas.toBlob(value => { if (value) finish(value); }, type, quality); }
      catch (error) { clearTimeout(timer); reject(error); }
    });
  }
  function create(options) {
    const root = options.root;
    if (!root || !global.crypto?.getRandomValues || !global.AbortController) return null;
    const el = name => root.querySelector('[data-room="' + name + '"]');
    const metrics = { oda_surumu: 'room-v1-20260910', oda_kolu: 'live-v1', oda_ziyaret_id: newRequest().id };
    const emit = (name, extra, flowEntry = entry) => {
      // Only this closed vocabulary leaves the editor. No photo URL, text, coordinates or token.
      const properties = { ...metrics, giris: flowEntry, kaynak: sourceKind };
      for (const key of ['istek_id', 'tasarim_id', 'saniye', 'sebep', 'asama', 'taslak_degisti', 'model']) {
        if (extra && extra[key] !== undefined) properties[key] = extra[key];
      }
      try { options.track(name, properties); } catch (_) { /* analytics never blocks the flow */ }
    };
    let entry = 'early', sourceKind = 'none', phase = 'empty';
    const seenInvites = new Set();
    function markInvite(inviteEntry, reason) {
      if (seenInvites.has(inviteEntry)) return;
      seenInvites.add(inviteEntry); emit('wam_oda_davet_goruldu', { sebep: reason }, inviteEntry);
    }
    let photoFile = null, photoUrl = '', outputUrl = '', cropUrl = '';
    let area = initialArea(), draftRevision = 0, snapshotRevision = 0, snapshotArea = area;
    let selected = false, lateDesign = null, request = null, result = null, busy = false;
    let controller = null, imageEpoch = 0, visibleResult = false, cropEpoch = 0, previousFocus = null, expiryTimer = null;
    let cropReady = false, reviewedArea = null, lockedCrop = null, visibleSince = 0, visibilityTimer = null;
    const objectUrls = new Set();
    const urlFor = blob => { const url = URL.createObjectURL(blob); objectUrls.add(url); return url; };
    const revoke = url => { if (url && objectUrls.delete(url)) URL.revokeObjectURL(url); };
    function protectReplay() {
      const ph = global.posthog;
      if (!ph?.config || typeof ph.set_config !== 'function') return;
      const config = ph.config.session_recording || {};
      if (config.maskCapturedNetworkRequestFn?.wamRoomMask) return;
      const prior = config.maskCapturedNetworkRequestFn;
      const mask = request => {
        if (/\/api\/room-preview(?:\/|$|\?)/.test(request.name || '') || /^(data:|blob:)/.test(request.name || '')) return null;
        const safe = prior ? prior(request) : request;
        return safe ? { ...safe, requestBody: undefined, responseBody: undefined } : null;
      };
      mask.wamRoomMask = true;
      // Rebind the active network plugin before photo traffic. A stopped recorder stays stopped.
      const recording = ph.sessionRecordingStarted?.() === true;
      if (recording) ph.stopSessionRecording();
      ph.set_config({ session_recording: { ...config, maskCapturedNetworkRequestFn: mask } });
      if (recording) ph.startSessionRecording();
    }
    const status = (text, error = false) => { el('status').textContent = text; el('status').classList.toggle('wam-room__error', error); };
    function draw() {
      Object.assign(el('selection').style, { left: area.x * 100 + '%', top: area.y * 100 + '%', width: area.width * 100 + '%', height: area.height * 100 + '%' });
      const bounds = el('selection').getBoundingClientRect();
      // Overlapping 44px corner targets must not take the centre's move gesture.
      el('selection').toggleAttribute('data-compact', bounds.width < 88 || bounds.height < 88);
      root.querySelectorAll('[data-axis]').forEach(input => { input.value = Math.round(area[input.dataset.axis] * 100); });
    }
    function changed(next) {
      if (phase === 'crop' && lockedCrop) return;
      area = constrain(next); draftRevision++; draw();
      emit('wam_oda_alan_isaretlendi', { asama: phase === 'crop' ? 'crop' : 'placement' });
      if (phase === 'crop') { el('crop-consent').checked = false; updateCrop(); }
      else if (busy) status('Your new position is saved for the next preview. The preview in progress uses the previous position.');
    }
    function showEditor() {
      el('invite').hidden = true; el('editor').hidden = false;
      el('workspace').hidden = !photoFile && !outputUrl;
      root.scrollIntoView({ block: 'start', behavior: 'auto' });
    }
    function sync() {
      const showingOutput = phase === 'result' || phase === 'crop';
      const cropLocked = phase === 'crop' && (busy || !!lockedCrop);
      el('workspace').hidden = !photoFile && !outputUrl;
      el('selection').hidden = phase === 'result';
      el('area-controls').hidden = phase === 'result';
      el('transfer').hidden = showingOutput;
      el('crop-review').hidden = phase !== 'crop';
      el('pickers').hidden = showingOutput;
      el('intro').hidden = showingOutput;
      el('adjust').hidden = !showingOutput;
      el('existing').hidden = phase !== 'editing' || !result || !outputUrl || !request;
      el('existing').disabled = busy;
      el('back').hidden = !lateDesign || busy;
      el('apply').hidden = phase === 'result' && !!lateDesign;
      el('apply').disabled = busy || (phase === 'crop' && (!cropReady || !el('crop-consent').checked));
      el('crop-consent').disabled = busy || !cropReady || !!lockedCrop;
      el('remove').disabled = busy && phase === 'crop';
      el('adjust').disabled = cropLocked;
      root.querySelectorAll('[data-axis], [data-corner], [data-room="ratio"]').forEach(input => { input.disabled = cropLocked; });
      el('apply').textContent = phase === 'recovering' ? 'Check preview' : phase === 'crop' ? 'Save artwork' : phase === 'result' ? 'Review artwork' : lateDesign ? 'Preview here' : 'Use this wall';
      el('heading').textContent = phase === 'crop' ? 'Choose what to save' : showingOutput ? 'Your design, your room' : 'Choose your wall';
      el('area-title').textContent = phase === 'crop' ? 'Select the whole artwork' : 'Where should it go?';
      el('area-help').textContent = phase === 'crop' ? 'Only this crop will become your saved design.' : 'Move the box and resize it to mark the space. This suggests a position, not a measurement.';
      draw();
    }
    async function loadFile(file, kind) {
      if (!file) { emit('wam_oda_secim_iptal', { asama: 'picker' }); return; }
      sourceKind = kind; protectReplay(); emit('wam_oda_dosya_secildi');
      if (file.size > MAX_FILE) { status('Choose a photo smaller than 10 MB.', true); return; }
      const epoch = ++imageEpoch; let url = urlFor(file); const probe = new Image();
      try {
        probe.src = url; await probe.decode();
        if (epoch !== imageEpoch) { revoke(url); return; }
        if (probe.naturalWidth * probe.naturalHeight > 40000000) throw new Error('too large');
        if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
          const scale = Math.min(1, 2048 / Math.max(probe.naturalWidth, probe.naturalHeight));
          const canvas = document.createElement('canvas');
          canvas.width = Math.max(1, Math.round(probe.naturalWidth * scale)); canvas.height = Math.max(1, Math.round(probe.naturalHeight * scale));
          canvas.getContext('2d').drawImage(probe, 0, 0, canvas.width, canvas.height);
          const blob = await canvasBlob(canvas, 'image/jpeg', () => epoch === imageEpoch, .9);
          if (epoch !== imageEpoch) { revoke(url); return; }
          if (!blob || !blob.size) throw new Error('photo encoding failed');
          file = new File([blob], 'room.jpg', { type: 'image/jpeg' });
          revoke(url); url = urlFor(file);
        }
        revoke(photoUrl); photoUrl = url; photoFile = file; sourceKind = kind;
        selected = false; area = initialArea(); draftRevision++; phase = 'editing';
        el('consent').checked = false; el('photo').src = photoUrl;
        el('photo').alt = 'Your room photo with an adjustable artwork area';
        showEditor(); sync(); status('Mark an area, then choose Use this wall. You can still skip this step.');
        emit('wam_oda_dosya_acildi');
      } catch (_) {
        revoke(url); if (epoch !== imageEpoch) return;
        status('This photo could not be opened. Try JPEG or PNG, or take another photo.', true); emit('wam_oda_hata', { sebep: 'photo_decode' });
      }
    }
    const authHeaders = () => ({ Authorization: 'Bearer ' + request.token });
    async function jsonRequest(path, body, signal) {
      protectReplay();
      const response = await fetch(options.api + '/api/room-preview' + path, {
        method: 'POST', headers: body instanceof FormData ? authHeaders() : { ...authHeaders(), 'Content-Type': 'application/json' },
        body: body instanceof FormData ? body : JSON.stringify(body || {}), signal, cache: 'no-store', referrerPolicy: 'no-referrer'
      });
      const data = await response.json();
      if (!response.ok) {
        const error = new Error(data.code || 'ROOM_PREVIEW_FAILED');
        error.publicCropRetained = data.public_crop_retained === true;
        error.publicCropUnknown = data.public_crop_retained === null;
        throw error;
      }
      return data;
    }
    function newRequest() {
      const bytes = new Uint8Array(32); crypto.getRandomValues(bytes);
      const uuid = new Uint8Array(16); crypto.getRandomValues(uuid); uuid[6] = (uuid[6] & 15) | 64; uuid[8] = (uuid[8] & 63) | 128;
      const hex = Array.from(uuid, b => b.toString(16).padStart(2, '0')).join('');
      return { id: [hex.slice(0, 8), hex.slice(8, 12), hex.slice(12, 16), hex.slice(16, 20), hex.slice(20)].join('-'), token: Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('') };
    }
    async function display(data, call) {
      if (request !== call || !data.preview_data) return;
      if (!/^data:image\/(png|jpeg);base64,/.test(data.preview_data) || data.preview_data.length > MAX_FILE * 1.4) throw new Error('Invalid preview');
      if (!Number.isFinite(data.expires_in_ms)) throw new Error('Missing preview lifetime');
      const [prefix, encoded] = data.preview_data.split(',');
      const bytes = Uint8Array.from(atob(encoded), c => c.charCodeAt(0));
      const blob = new Blob([bytes], { type: prefix.includes('png') ? 'image/png' : 'image/jpeg' });
      if (request !== call) return;
      revoke(outputUrl); outputUrl = urlFor(blob); result = data; phase = 'result'; visibleResult = !!call.visibleRecorded;
      visibleSince = 0; clearTimeout(visibilityTimer);
      clearTimeout(expiryTimer);
      if (call.expiresAt === undefined) call.expiresAt = performance.now() + Math.min(900000, Math.max(0, data.expires_in_ms));
      const expire = () => {
        if (request !== call) return;
        // Do not erase the accepted request while its bounded save is returning.
        if (busy && phase === 'crop') { expiryTimer = setTimeout(expire, 1000); return; }
        remove(false); el('intro').textContent = 'The temporary preview expired. Any saved artwork remains. Choose a photo again, or continue without it.';
      };
      expiryTimer = setTimeout(expire, Math.max(0, call.expiresAt - performance.now()));
      el('photo').onload = () => {
        if (phase === 'result') reportVisible();
      };
      el('photo').src = outputUrl; el('photo').alt = 'Approximate preview of your artwork in your room';
      sync();
      status(draftRevision !== snapshotRevision ? 'This preview uses the previous photo and position. Your new selection is still available under Adjust placement.' : 'Check the artwork and its placement. Size and perspective are approximate.');
      root.scrollIntoView({ block: 'start', behavior: 'auto' });
    }
    function reportVisible() {
      const img = el('photo'), rect = img.getBoundingClientRect();
      const visibleHeight = Math.max(0, Math.min(innerHeight, rect.bottom) - Math.max(0, rect.top));
      const visibleWidth = Math.max(0, Math.min(innerWidth, rect.right) - Math.max(0, rect.left));
      const fraction = rect.width * rect.height ? visibleHeight * visibleWidth / (rect.width * rect.height) : 0;
      if (!result || phase !== 'result' || document.visibilityState !== 'visible' || !img.complete || !img.naturalWidth || fraction < .25) {
        visibleSince = 0; clearTimeout(visibilityTimer); return;
      }
      if (visibleResult) return;
      if (!visibleSince) visibleSince = performance.now();
      const remaining = 1000 - (performance.now() - visibleSince);
      if (remaining > 0) { clearTimeout(visibilityTimer); visibilityTimer = setTimeout(reportVisible, remaining); return; }
      visibleResult = true; request.visibleRecorded = true;
      emit('wam_oda_onizleme_goruldu', { istek_id: request.id, tasarim_id: result.design_id, saniye: (performance.now() - request.started) / 1000, taslak_degisti: draftRevision !== snapshotRevision });
      if (!lateDesign) {
        emit('wam_sonuc_gorundu', { istek_id: request.id, tasarim_id: result.design_id, asama: 'room' });
        options.onFirstResultSeen?.(result.design_id);
      }
    }
    if (global.IntersectionObserver) {
      const observer = new IntersectionObserver(reportVisible, { threshold: .25 }); observer.observe(el('photo'));
    } else global.addEventListener('scroll', reportVisible, { passive: true });
    document.addEventListener('visibilitychange', reportVisible);
    function ready(data, call) {
      if (call.readyRecorded) return;
      call.readyRecorded = true;
      emit(lateDesign ? 'wam_oda_istek_hazir' : 'wam_tasarim_hazir', { istek_id: call.id, tasarim_id: data.design_id, saniye: (performance.now() - call.started) / 1000, model: data.model, asama: lateDesign ? 'room_placement' : 'room' });
    }
    async function recover() {
      if (busy || !request) return false;
      const call = request; busy = true; sync();
      try {
        const data = await jsonRequest('/' + call.id + '/status', {}, AbortSignal.timeout(15000));
        if (request !== call) return false;
        if (data.success && data.status === 'confirmed' && !lateDesign && data.preview_url) {
          await options.onConfirmed(data); await remove(false); return true;
        }
        if (data.success && data.preview_data) { ready(data, call); await display(data, call); return true; }
        if (data.status === 'processing') status('The preview is still processing. You can skip it at any time.');
        else status('The preview could not be completed. Choose a photo again, or skip this step.', true);
      } catch (_) { if (request === call) status('This temporary preview is no longer available. Choose a photo again, or skip this step.', true); }
      finally { if (request === call) { busy = false; sync(); } }
      return false;
    }
    async function generate(context) {
      if (phase === 'recovering') return recover();
      if (busy || !photoFile) return false;
      if (!el('consent').checked) { showEditor(); status('Choose whether to send this photo, or skip this step.', true); el('consent').focus(); return false; }
      const call = newRequest(); request = call; call.started = performance.now();
      snapshotArea = { ...area }; snapshotRevision = draftRevision; busy = true; controller = new AbortController();
      revoke(outputUrl); outputUrl = ''; result = null; lockedCrop = null;
      const callController = controller;
      sync(); showEditor(); status('Making your room preview. You can adjust the area while you wait.');
      emit('wam_oda_istek_basladi', { istek_id: call.id });
      if (!lateDesign) emit('wam_tasarim_istendi', { istek_id: call.id, asama: 'room' });
      const form = new FormData();
      form.append('istek_id', call.id); form.append('room', photoFile); form.append('area', JSON.stringify(snapshotArea));
      form.append('transfer_consent', 'yes'); form.append('entry', lateDesign ? 'late' : 'early');
      form.append('category', context.category); form.append('mood', context.mood || ''); form.append('text', context.text || '');
      if (context.scene) form.append('scene', context.scene);
      if (lateDesign) form.append('design_id', lateDesign.id);
      else if (context.file) form.append('image', context.file);
      const timeout = setTimeout(() => callController.abort(), 550000);
      try {
        let data;
        try { data = await jsonRequest('', form, controller.signal); }
        catch (error) {
          if (request !== call) return false;
          // Recovery asks for the same result. It never starts a second paid generation.
          data = await jsonRequest('/' + call.id + '/status', {}, AbortSignal.timeout(15000));
          if (data.status === 'processing') { status('The preview is still processing. Choose Check preview in a moment.'); el('apply').textContent = 'Check preview'; phase = 'recovering'; return false; }
          if (!data.success) throw error;
        }
        if (request !== call) return false;
        if (data.status === 'processing') { phase = 'recovering'; status('The preview is still processing. Choose Check preview in a moment.'); return false; }
        if (!data.success || !data.preview_data) throw new Error('Missing preview');
        ready(data, call);
        await display(data, call); return true;
      } catch (_) {
        if (request === call) { phase = 'recovering'; status('The room preview did not come through. Check preview retrieves the same attempt, or skip it and continue.', true); emit('wam_oda_hata', { istek_id: call.id, sebep: 'preview_failed' }); }
        return false;
      } finally { clearTimeout(timeout); if (request === call) { busy = false; controller = null; sync(); if (phase === 'recovering') el('apply').textContent = 'Check preview'; } }
    }
    async function updateCrop() {
      const epoch = ++cropEpoch;
      cropReady = false; reviewedArea = null; el('crop-consent').checked = false; sync();
      el('crop-image').hidden = true;
      const chosen = { ...area };
      try {
      const img = el('photo'); if (!img.complete || !img.naturalWidth) await img.decode();
      if (epoch !== cropEpoch || phase !== 'crop') return;
      const x = Math.floor(chosen.x * img.naturalWidth), y = Math.floor(chosen.y * img.naturalHeight);
      const w = Math.max(1, Math.min(img.naturalWidth - x, Math.round(chosen.width * img.naturalWidth)));
      const h = Math.max(1, Math.min(img.naturalHeight - y, Math.round(chosen.height * img.naturalHeight)));
      if (x === 0 && y === 0 && w === img.naturalWidth && h === img.naturalHeight) {
        status('Choose the artwork area to save, rather than the whole room photo.', true); return;
      }
      // Review only: no product redrawing, alpha mask or AR. The server crops the same pixels.
      const canvas = document.createElement('canvas'); canvas.width = w; canvas.height = h;
      canvas.getContext('2d').drawImage(img, x, y, w, h, 0, 0, w, h);
      const blob = await canvasBlob(canvas, 'image/png', () => epoch === cropEpoch && phase === 'crop');
      if (!blob || epoch !== cropEpoch || phase !== 'crop') return;
      revoke(cropUrl); cropUrl = urlFor(blob); el('crop-image').src = cropUrl;
      await el('crop-image').decode();
      if (epoch !== cropEpoch || phase !== 'crop') return;
      reviewedArea = chosen; cropReady = true; el('crop-image').hidden = false; sync();
      } catch (_) {
        if (epoch === cropEpoch) {
          revoke(cropUrl); cropUrl = ''; el('crop-image').removeAttribute('src');
          status('The crop could not be displayed. Adjust the area to try again, or skip this step.', true);
        }
      }
    }
    async function confirm() {
      if (!cropReady || !reviewedArea || !el('crop-consent').checked || busy) return;
      const call = request; const saveController = new AbortController();
      if (!lockedCrop) lockedCrop = { ...reviewedArea };
      const timeout = setTimeout(() => saveController.abort(), 30000);
      busy = true; sync(); status('Saving the exact crop you reviewed.');
      try {
        const data = await jsonRequest('/' + call.id + '/confirm', { area: lockedCrop, public_crop_consent: true }, saveController.signal);
        if (request !== call) return;
        emit('wam_oda_urun_onaylandi', { istek_id: call.id, tasarim_id: data.design_id });
        await options.onConfirmed(data);
        await remove(false);
      } catch (error) {
        if (request !== call) return;
        if (error.message === 'SELECT_ARTWORK_CROP') lockedCrop = null;
        status(error.message === 'SELECT_ARTWORK_CROP' ? 'Select the artwork area to save.'
          : error.publicCropRetained ? 'The crop was stored, but its design link could not be confirmed. Try again to recover it. Remove photo only clears the temporary preview.'
          : error.publicCropUnknown ? 'The crop may already be stored. Try again to recover the same file. Remove photo only clears the temporary preview.'
          : 'Saving could not be confirmed. Try again to check the same design.', true);
      }
      finally { clearTimeout(timeout); if (request === call) { busy = false; sync(); } }
    }
    async function remove(track = true) {
      const previous = request; request = null; imageEpoch++; cropEpoch++; controller?.abort(); controller = null;
      clearTimeout(expiryTimer); expiryTimer = null;
      clearTimeout(visibilityTimer); visibleSince = 0;
      busy = false; selected = false; photoFile = null; result = null; phase = 'empty'; lateDesign = null; cropReady = false; reviewedArea = null; lockedCrop = null;
      el('photo').removeAttribute('src'); el('crop-image').removeAttribute('src');
      el('crop-image').hidden = true;
      for (const url of objectUrls) URL.revokeObjectURL(url); objectUrls.clear(); photoUrl = outputUrl = cropUrl = '';
      el('camera-input').value = ''; el('gallery-input').value = ''; el('consent').checked = el('crop-consent').checked = false;
      el('editor').hidden = true; el('invite').hidden = false; sync(); status('');
      if (track) emit('wam_oda_silme_istendi');
      if (previous) {
        try { await fetch(options.api + '/api/room-preview/' + previous.id + '/remove', { method: 'POST', headers: { Authorization: 'Bearer ' + previous.token }, cache: 'no-store', referrerPolicy: 'no-referrer', keepalive: true }); } catch (_) { /* provider/server expiry remains, no deletion guarantee */ }
      }
    }
    function leave() { emit('wam_oda_atlandi', { asama: busy ? 'waiting' : phase }); remove(false); root.hidden = true; options.onReturn?.(); previousFocus?.focus(); }
    el('open').onclick = () => { previousFocus = document.activeElement; entry = 'early'; markInvite('early', 'interaction'); showEditor(); emit('wam_oda_acildi'); el('gallery').focus(); };
    el('skip').onclick = () => { entry = 'early'; markInvite('early', 'interaction'); emit('wam_oda_atlandi', { asama: 'invitation' }); root.hidden = true; };
    el('leave').onclick = leave; el('remove').onclick = () => remove();
    el('camera').onclick = () => { sourceKind = 'camera'; el('camera-input').value = ''; el('camera-input').click(); };
    el('gallery').onclick = () => { sourceKind = 'gallery'; el('gallery-input').value = ''; el('gallery-input').click(); };
    el('camera-input').onchange = e => loadFile(e.target.files[0], 'camera');
    el('gallery-input').onchange = e => loadFile(e.target.files[0], 'gallery');
    for (const name of ['camera-input', 'gallery-input']) el(name).addEventListener('cancel', () => emit('wam_oda_secim_iptal', { asama: 'picker' }));
    el('crop-consent').onchange = sync;
    el('apply').onclick = async () => {
      if (phase === 'recovering') {
        await recover();
      } else if (phase === 'result') { phase = 'crop'; area = { ...snapshotArea }; el('crop-consent').checked = false; sync(); await updateCrop(); }
      else if (phase === 'crop') await confirm();
      else if (!el('consent').checked) { status('Choose whether to send this photo to make a preview, or skip this step.', true); el('consent').focus(); }
      else if (lateDesign) { emit('wam_oda_aktarim_secildi'); emit('wam_oda_alan_isaretlendi', { asama: 'placement_confirmed' }); await generate({ category: lateDesign.category, text: options.context().text, mood: lateDesign.mood }); }
      else { selected = true; emit('wam_oda_aktarim_secildi'); emit('wam_oda_alan_isaretlendi', { asama: 'placement_confirmed' }); status('Wall selected. Continue with your source photo or text below.'); options.onWallSelected?.(); }
    };
    el('existing').onclick = async () => { if (!busy && result && request) await display(result, request); };
    el('adjust').onclick = () => { phase = 'editing'; el('photo').src = photoUrl; sync(); status('Your next preview will use this position. Adjusting it does not start a generation.'); };
    el('back').onclick = () => { emit('wam_oda_tasarima_donuldu', { tasarim_id: lateDesign?.id }); root.hidden = true; options.onReturn?.(); };
    root.querySelectorAll('[data-axis]').forEach(input => {
      input.oninput = () => {
        const next = { ...area, [input.dataset.axis]: Number(input.value) / 100 };
        if (el('ratio').checked && ['width', 'height'].includes(input.dataset.axis)) {
          if (input.dataset.axis === 'width') next.height = next.width * area.height / area.width;
          else next.width = next.height * area.width / area.height;
        }
        changed(next);
      };
    });
    let drag = null;
    if (global.ResizeObserver) new ResizeObserver(draw).observe(el('frame'));
    el('selection').onpointerdown = e => {
      if (e.button !== 0 || ((busy || lockedCrop) && phase === 'crop')) return;
      if (phase === 'crop') { cropEpoch++; cropReady = false; el('crop-consent').checked = false; sync(); }
      e.preventDefault(); el('selection').setPointerCapture(e.pointerId); drag = { x: e.clientX, y: e.clientY, area: { ...area }, corner: e.target.dataset.corner };
    };
    el('selection').onpointermove = e => {
      if (!drag) return;
      const rect = el('frame').getBoundingClientRect(); const dx = (e.clientX - drag.x) / rect.width, dy = (e.clientY - drag.y) / rect.height;
      let next = { ...drag.area };
      if (drag.corner === 'end') { next.width += dx; next.height += dy; }
      else if (drag.corner === 'start') { next.x += dx; next.y += dy; next.width -= dx; next.height -= dy; }
      else { next.x += dx; next.y += dy; }
      if (drag.corner && el('ratio').checked) next.height = next.width * drag.area.height / drag.area.width;
      area = constrain(next); draw();
    };
    el('selection').onpointerup = () => { if (drag) { drag = null; changed(area); } };
    el('selection').onpointercancel = () => { if (drag) { drag = null; changed(area); } };
    el('selection').onkeydown = e => {
      if ((busy || lockedCrop) && phase === 'crop') return;
      if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.key)) return;
      e.preventDefault(); const next = { ...area };
      next[e.key === 'ArrowLeft' || e.key === 'ArrowRight' ? 'x' : 'y'] += ['ArrowLeft', 'ArrowUp'].includes(e.key) ? -.01 : .01;
      changed(next);
    };
    emit('wam_oda_uygun'); emit('wam_oda_atandi');
    if (global.IntersectionObserver) {
      const invitations = new Map([[el('open'), 'early']]);
      if (options.lateInvite) invitations.set(options.lateInvite, 'late');
      const inviteObserver = new IntersectionObserver(entries => {
        for (const item of entries) if (item.isIntersecting && item.intersectionRatio >= .5) {
          markInvite(invitations.get(item.target), 'visibility'); inviteObserver.unobserve(item.target);
        }
      }, { threshold: .5 });
      for (const target of invitations.keys()) inviteObserver.observe(target);
    }
    global.addEventListener('pagehide', () => remove(false));
    return {
      metrics: () => ({ ...metrics }),
      shouldGenerate: opts => selected && photoFile && !opts.isVariation,
      generate: context => generate({ ...options.context(), ...context }),
      openLate: design => {
        if (!design || busy) return;
        lateDesign = { ...design }; entry = 'late'; el('consent').checked = false; root.hidden = false; previousFocus = document.activeElement;
        markInvite('late', 'interaction');
        phase = photoFile ? 'editing' : 'empty'; if (photoUrl) el('photo').src = photoUrl;
        showEditor(); sync(); emit('wam_oda_acildi', { tasarim_id: design.id }); el('gallery').focus();
      },
      remove
    };
  }
  global.WamRoomPreview = { create };
})(window);
